import { Popover as HUIPopover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { Fragment, memo } from 'react';

import { useDeleteSequence } from '@/hooks/useScheduleExpectations';
import EndDateSelection from '@/pages/Scheduling/components/EndDateSelection';
import { ChronologicalScheduleExpectation } from '@/types/scheduleExpectation';
import { Button as ButtonV2, Timeline, TimelineItem } from '@/ui';
import { formatShortDate } from '@/utils/dates';

import ExpectationDetailsAccordion from './ExpectationDetailsAccordion';

interface Props {
  item: ChronologicalScheduleExpectation;
  activeExpectationId?: string | null;
  phaseId: string;
}

const ScheduleExpectationSequenceItemDetail = ({ item, activeExpectationId, phaseId }: Props) => {
  const { sequence, expectations, end_at } = item;

  const { mutateAsync: deleteSequence, isPending: isDeleting } = useDeleteSequence(phaseId);

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this schedule expectation sequence?')) return;
    await deleteSequence({ id: item.object_id });
  };

  if (!sequence) return null;

  const hasEndDate = !!end_at;

  return (
    <div>
      <ExpectationDetailsAccordion
        note={sequence.note}
        createdBy={sequence.created_by}
        createdAt={sequence.created_at}
      />

      <div className="mt-3">
        <Timeline>
          {expectations.map((expectation, index) => {
            const expectationMatchId =
              expectation.custom_schedule_expectation_id || expectation.schedule_expectation_id;
            const isExpectationActive =
              !!activeExpectationId &&
              !!expectationMatchId &&
              expectationMatchId === activeExpectationId;
            return (
              <TimelineItem
                key={
                  expectation.custom_schedule_expectation_id ||
                  expectation.schedule_expectation_id ||
                  index
                }
                color="secondary"
                date={expectation.start_at ? formatShortDate(expectation.start_at) : undefined}
                data-testid={isExpectationActive ? 'active-expectation-detail' : undefined}
                className={isExpectationActive ? 'ring-2 ring-blue-400 rounded-lg' : ''}>
                <p className="text-sm text-uniform-gray-700 dark:text-uniform-gray-300">
                  {expectation.description}
                </p>
                {expectation.custom_schedule_expectation_id && (
                  <div className="mt-1.5">
                    <ExpectationDetailsAccordion
                      note={expectation.note}
                      createdBy={expectation.created_by}
                      createdAt={expectation.created_at}
                    />
                  </div>
                )}
              </TimelineItem>
            );
          })}
        </Timeline>
      </div>

      <div className="flex space-x-2 mt-3" onClick={(e) => e.stopPropagation()}>
        {!hasEndDate && (
          <HUIPopover className="relative">
            <PopoverButton as={Fragment}>
              <ButtonV2 variant="subtle" color="warning" size="xs">
                End
              </ButtonV2>
            </PopoverButton>
            <PopoverPanel className="absolute left-0 z-20 mt-1 w-72 rounded-lg border border-uniform-gray-200 dark:border-uniform-gray-700 bg-white dark:bg-uniform-gray-900 shadow-lg p-3">
              <EndDateSelection
                customScheduleExpectationId={
                  expectations[0]?.custom_schedule_expectation_id || item.object_id
                }
                phaseId={phaseId}
                isSequence
                sequenceId={item.object_id}
              />
            </PopoverPanel>
          </HUIPopover>
        )}

        <ButtonV2
          variant="default"
          color="destructive"
          size="xs"
          disabled={isDeleting}
          onClick={handleDelete}>
          Delete
        </ButtonV2>
      </div>
    </div>
  );
};

export default memo(ScheduleExpectationSequenceItemDetail);
